import { downloadMultiSectionCsv, printHtml, _esc } from "@/lib/csv";
import { formatDate } from "@/lib/format";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const lineTotal = (l) => (l.unit_cost || 0) * (l.quantity || 0);
const status = (s) => (s || "").replace("_", " ").toUpperCase();
const subsOf = (parent, sub) => (sub ? [sub] : parent.sub_tasks || []);
const grandTotal = (subs) => subs.reduce((a, s) => a + (s.total_cost || 0), 0);

const descList = (items) => (Array.isArray(items) ? items : []).filter((d) => (d.text || "").trim());

export function exportWoCsv(parent, sub = null) {
    const subs = subsOf(parent, sub);
    const parts = subs.flatMap((s) => (s.parts_issued || []).map((l) => ({ ...l, sub_wo: s.wo_number })));
    // Row-wise work descriptions: one row per entry (parent + each sub), not a
    // joined cell. Reads cleanly in Excel and lets users filter/sort by date.
    const parentDescs = descList(parent.descriptions).map((d) => ({
        source: `MAIN · ${parent.wo_number}`,
        date: d.date || "",
        text: d.text || "",
    }));
    const subDescs = subs.flatMap((s) => descList(s.descriptions).map((d) => ({
        source: `SUB · ${s.wo_number}`,
        date: d.date || "",
        text: d.text || "",
    })));
    const descs = [...parentDescs, ...subDescs];
    const ref = sub ? sub.wo_number : parent.wo_number;
    downloadMultiSectionCsv(`work-order-${ref}.csv`, [
        {
            title: "Main work order",
            rows: [parent],
            columns: [
                ["MAIN WO #", "wo_number"], ["VEHICLE", (p) => p.vehicle_reg_number || ""],
                ["CREATED", (p) => p.creation_date || ""], ["STATUS", (p) => status(p.status)],
                ["SUB-TASKS FINISHED", (p) => `${p.completed_sub_count || 0}/${p.sub_task_count || 0}`],
                ["NOTES", (p) => p.notes || ""],
            ],
        },
        {
            title: sub ? "Sub-task" : "Sub-tasks",
            rows: subs,
            columns: [
                ["SUB WO #", "wo_number"], ["TITLE", "title"],
                ["TYPE", "type"], ["PRIORITY", "priority"], ["STATUS", (s) => status(s.status)],
                ["START", (s) => s.start_date || ""], ["FINISH", (s) => s.end_date || ""],
                ["PARTS COUNT", (s) => (s.parts_issued || []).length], ["COST (INR)", (s) => s.total_cost || 0],
            ],
        },
        {
            title: "Work descriptions (dated)",
            rows: descs,
            columns: [
                ["WORK ORDER", "source"],
                ["DATE", "date"],
                ["DESCRIPTION", "text"],
            ],
        },
        {
            title: "Parts issued",
            rows: parts,
            columns: [
                ["SUB WO #", "sub_wo"], ["PART", (l) => l.name || l.inventory_id || ""],
                ["QTY", "quantity"], ["UNIT COST (INR)", (l) => l.unit_cost || 0], ["LINE TOTAL (INR)", lineTotal],
            ],
        },
        { title: "Total", rows: [{ t: grandTotal(subs) }], columns: [["TOTAL COST (INR)", "t"]] },
    ]);
}

const field = (label, value) =>
    `<div class="f"><div class="fl">${_esc(label)}</div><div class="fv">${_esc(value || "—")}</div></div>`;

const descriptionsBlock = (items, label = "Work descriptions") => {
    const list = descList(items);
    if (!list.length) return "";
    const rows = list.map((d) => `<li><span class="dl-date">${_esc(formatDate(d.date) || "—")}</span>
        <span class="dl-text">${_esc(d.text)}</span></li>`).join("");
    return `<div class="desc"><div class="fl">${_esc(label)}</div>
        <ul class="desc-list">${rows}</ul></div>`;
};

const partsTable = (s) => {
    const lines = s.parts_issued || [];
    if (!lines.length) return `<p class="empty">No parts issued.</p>`;
    const rows = lines.map((l, i) => `<tr><td>${i + 1}</td><td class="part-name">${_esc(l.name || l.inventory_id)}</td>
        <td class="n">${_esc(l.quantity)}</td><td class="n">${_esc(inr(l.unit_cost))}</td>
        <td class="n">${_esc(inr(lineTotal(l)))}</td></tr>`).join("");
    return `<div class="parts-wrap"><div class="fl">Items used</div>
        <table class="parts-table"><thead><tr><th>#</th><th>Part</th><th class="n">Qty</th><th class="n">Unit cost</th>
        <th class="n">Line total</th></tr></thead><tbody>${rows}</tbody>
        <tfoot><tr><td colspan="4" class="n"><b>Sub-task total</b></td><td class="n"><b>${_esc(inr(s.total_cost))}</b></td></tr></tfoot></table></div>`;
};

const subSection = (s) => `<section class="sub">
    <h2>${_esc(s.wo_number)} · ${_esc(s.title)}</h2>
    <div class="grid">${field("Type", s.type)}${field("Priority", s.priority)}${field("Status", status(s.status))}
        ${field("Start", formatDate(s.start_date))}${field("Finish", s.end_date ? formatDate(s.end_date) : "")}</div>
    ${s.description ? `<div class="desc"><div class="fl">Description</div><div class="desc-body">${_esc(s.description)}</div></div>` : ""}
    ${descriptionsBlock(s.descriptions, "Work descriptions (dated)")}
    ${partsTable(s)}
</section>`;

const CARD_CSS = `
    .hdr { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:2px solid #0f172a; padding-bottom:8px; margin-bottom:12px; }
    .brand { font-size:11px; letter-spacing:.2em; color:#64748b; }
    h1 { font-size:20px; margin:2px 0 0; }
    .meta { font-size:10px; color:#64748b; text-align:right; }
    .grid { display:grid; grid-template-columns:repeat(5,1fr); gap:6px 12px; margin:6px 0 10px; }
    .f .fl, .desc .fl, .parts-wrap .fl { font-size:9px; text-transform:uppercase; letter-spacing:.08em; color:#64748b; margin-bottom:3px; }
    .f .fv { font-size:12px; font-weight:600; }
    /* Work descriptions and legacy description text — larger, airier type
     * (1.15 line-height, 13px) so technicians can read what was done on each
     * date and which items were used at a glance in the printed PDF. */
    .desc { margin-bottom:10px; }
    .desc-body { font-size:13px; line-height:1.15; white-space:pre-wrap; color:#0f172a; }
    .desc-list { list-style:none; padding:0; margin:0; }
    .desc-list li { font-size:13px; line-height:1.15; margin:0 0 4px; display:flex; gap:10px; align-items:flex-start; }
    .desc-list .dl-date { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:11px; color:#64748b; min-width:88px; padding-top:1px; }
    .desc-list .dl-text { font-size:13px; line-height:1.15; white-space:pre-wrap; flex:1; }
    .parts-wrap { margin-top:6px; }
    .parts-table { width:100%; border-collapse:collapse; font-size:12px; line-height:1.15; }
    .parts-table th, .parts-table td { border:1px solid #cbd5e1; padding:5px 7px; text-align:left; vertical-align:top; }
    .parts-table th { background:#f1f5f9; text-transform:uppercase; letter-spacing:.04em; font-size:9.5px; }
    .parts-table .part-name { font-size:13px; line-height:1.15; }
    .sub { border:1px solid #e2e8f0; border-radius:6px; padding:10px 12px; margin-top:12px; page-break-inside:avoid; }
    h2 { font-size:13px; margin:0 0 4px; }
    .n { text-align:right; }
    .empty { font-size:11px; color:#94a3b8; font-style:italic; margin:0; }
    .grand { margin-top:14px; text-align:right; font-size:14px; font-weight:700; }
    .notes { font-size:11px; white-space:pre-wrap; }
`;

export function exportWoPdf(parent, sub = null) {
    const subs = subsOf(parent, sub);
    const title = sub ? `Sub-task ${sub.wo_number}` : `Work Order ${parent.wo_number}`;
    const body = `<div class="hdr"><div><div class="brand">EAZYFLEET · JOB CARD</div><h1>${_esc(title)}</h1></div>
        <div class="meta">Generated ${_esc(new Date().toLocaleString("en-IN"))}</div></div>
        <div class="grid">${field("Main WO #", parent.wo_number)}${field("Vehicle", parent.vehicle_reg_number)}
            ${field("Created", formatDate(parent.creation_date))}${field("Status", status(parent.status))}
            ${field("Sub-tasks finished", `${parent.completed_sub_count || 0}/${parent.sub_task_count || 0}`)}</div>
        ${parent.notes ? `<div class="desc"><div class="fl">Notes</div><div class="notes">${_esc(parent.notes)}</div></div>` : ""}
        ${sub ? "" : descriptionsBlock(parent.descriptions, "Main WO work descriptions (dated)")}
        ${subs.length ? subs.map(subSection).join("") : `<p class="empty">No sub-tasks.</p>`}
        <div class="grand">Total cost: ${_esc(inr(grandTotal(subs)))}</div>`;
    printHtml(`EAZYFLEET · ${title}`, body, CARD_CSS);
}

/* ──────────────────────────────────────────────────────────────────────
 * BATCH (paginated-report) exports: group each main WO with its sub-tasks.
 * Used by the Work Orders page toolbar PDF/CSV so output reads
 * "main → its subs → next main → its subs…" instead of
 * "all mains together, then all subs together".
 * ────────────────────────────────────────────────────────────────────── */

const BATCH_CSS = `
    .rpt-hdr { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:2px solid #0f172a; padding-bottom:8px; margin-bottom:12px; }
    .rpt-hdr .brand { font-size:11px; letter-spacing:.2em; color:#64748b; }
    .rpt-hdr h1 { font-size:18px; margin:2px 0 0; }
    .rpt-hdr .meta { font-size:10px; color:#64748b; text-align:right; }
    .wo-block { border:1px solid #cbd5e1; border-radius:6px; padding:10px 12px; margin-bottom:12px; page-break-inside:avoid; }
    .wo-head { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:6px; }
    .wo-head .no { font-size:13px; font-weight:700; }
    .wo-head .veh { font-size:11px; color:#334155; }
    .wo-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:4px 10px; margin:4px 0 6px; }
    .wo-grid .fl { font-size:9px; text-transform:uppercase; letter-spacing:.08em; color:#64748b; }
    .wo-grid .fv { font-size:11px; font-weight:600; }
    .wo-notes { font-size:10px; color:#475569; margin:2px 0 6px; white-space:pre-wrap; }
    .subs-table { width:100%; border-collapse:collapse; font-size:10px; margin-top:4px; }
    .subs-table th, .subs-table td { border:1px solid #cbd5e1; padding:4px 6px; text-align:left; vertical-align:top; }
    .subs-table th { background:#f1f5f9; text-transform:uppercase; letter-spacing:.04em; font-size:9px; }
    .subs-table .n { text-align:right; }
    .subs-table tfoot td { background:#f8fafc; font-weight:600; }
    /* Dated work descriptions attached to each WO — print at a larger, readable
     * body size with 1.15 line-height so the "what was done" and "items used"
     * sections are easy to scan from the paginated report. */
    .wo-descs { margin:6px 0 4px; padding:6px 8px; background:#f8fafc; border-left:3px solid #cbd5e1; }
    .wo-descs .fl { font-size:9px; text-transform:uppercase; letter-spacing:.08em; color:#64748b; margin-bottom:3px; }
    .wo-descs ul { list-style:none; padding:0; margin:0; }
    .wo-descs li { font-size:12px; line-height:1.15; margin:0 0 3px; display:flex; gap:8px; align-items:flex-start; }
    .wo-descs .dl-date { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:10.5px; color:#64748b; min-width:84px; padding-top:1px; }
    .wo-descs .dl-text { font-size:12px; line-height:1.15; white-space:pre-wrap; flex:1; }
    .empty { font-size:10px; color:#94a3b8; font-style:italic; margin:4px 0 0; }
    .rpt-grand { margin-top:10px; text-align:right; font-size:13px; font-weight:700; }
`;

const descBlockBatch = (items, label) => {
    const list = descList(items);
    if (!list.length) return "";
    const rows = list.map((d) => `<li><span class="dl-date">${_esc(formatDate(d.date) || "—")}</span>
        <span class="dl-text">${_esc(d.text)}</span></li>`).join("");
    return `<div class="wo-descs"><div class="fl">${_esc(label)}</div><ul>${rows}</ul></div>`;
};

const woBlock = (p) => {
    const subs = p.sub_tasks || [];
    const subRows = subs.flatMap((s) => {
        const base = `<tr>
            <td>${_esc(s.wo_number)}</td>
            <td>${_esc(s.title || "")}</td>
            <td>${_esc(s.type || "")}</td>
            <td>${_esc(s.priority || "")}</td>
            <td>${_esc(status(s.status))}</td>
            <td>${_esc(s.start_date ? formatDate(s.start_date) : "")}</td>
            <td>${_esc(s.end_date ? formatDate(s.end_date) : "")}</td>
            <td class="n">${_esc((s.parts_issued || []).length)}</td>
            <td class="n">${_esc(inr(s.total_cost))}</td>
        </tr>`;
        const dBlock = descBlockBatch(s.descriptions, `Work descriptions · ${s.wo_number}`);
        return dBlock ? [base, `<tr><td colspan="9" style="padding:0;border:1px solid #cbd5e1">${dBlock}</td></tr>`] : [base];
    }).join("");
    const parentTotal = grandTotal(subs);
    const subsTable = subs.length ? `<table class="subs-table">
        <thead><tr>
            <th>SUB WO #</th><th>TITLE</th><th>TYPE</th><th>PRIORITY</th><th>STATUS</th>
            <th>START</th><th>FINISH</th><th class="n">PARTS</th><th class="n">COST</th>
        </tr></thead>
        <tbody>${subRows}</tbody>
        <tfoot><tr><td colspan="8" class="n">Main WO total</td><td class="n">${_esc(inr(parentTotal))}</td></tr></tfoot>
    </table>` : `<p class="empty">No sub-tasks.</p>`;
    return `<section class="wo-block">
        <div class="wo-head">
            <div><div class="no">${_esc(p.wo_number)}</div><div class="veh">${_esc(p.vehicle_reg_number || "—")}</div></div>
            <div class="meta" style="font-size:10px;color:#64748b">${_esc(formatDate(p.creation_date))}</div>
        </div>
        <div class="wo-grid">
            ${field("Status", status(p.status))}
            ${field("Sub-tasks finished", `${p.completed_sub_count || 0}/${p.sub_task_count || 0}`)}
            ${field("Sub-task count", subs.length)}
            ${field("Total cost", inr(parentTotal))}
        </div>
        ${p.notes ? `<div class="wo-notes">${_esc(p.notes)}</div>` : ""}
        ${descBlockBatch(p.descriptions, "Main WO work descriptions")}
        ${subsTable}
    </section>`;
};

export function exportWosPdf(parents, { title = "Work Orders" } = {}) {
    const grand = parents.reduce((a, p) => a + grandTotal(p.sub_tasks || []), 0);
    const body = `<div class="rpt-hdr">
            <div><div class="brand">EAZYFLEET · WORK ORDERS</div><h1>${_esc(title)}</h1></div>
            <div class="meta">Generated ${_esc(new Date().toLocaleString("en-IN"))}<br/>${_esc(parents.length)} main WOs</div>
        </div>
        ${parents.length ? parents.map(woBlock).join("") : `<p class="empty">No work orders.</p>`}
        <div class="rpt-grand">Grand total: ${_esc(inr(grand))}</div>`;
    printHtml(`EAZYFLEET · ${title}`, body, BATCH_CSS);
}

export function exportWosCsv(parents, { filename = "work-orders.csv" } = {}) {
    const sections = [];
    parents.forEach((p) => {
        const subs = p.sub_tasks || [];
        const bannerTitle = p.vehicle_reg_number
            ? `Main WO ${p.wo_number} · ${p.vehicle_reg_number}`
            : `Main WO ${p.wo_number}`;
        sections.push({
            title: bannerTitle,
            rows: [p],
            columns: [
                ["MAIN WO #", "wo_number"],
                ["VEHICLE", (x) => x.vehicle_reg_number || ""],
                ["CREATED", (x) => x.creation_date || ""],
                ["STATUS", (x) => status(x.status)],
                ["SUB-TASKS FINISHED", (x) => `${x.completed_sub_count || 0}/${x.sub_task_count || 0}`],
                ["TOTAL COST (INR)", () => grandTotal(subs)],
                ["NOTES", (x) => x.notes || ""],
            ],
        });
        sections.push({
            title: `  └ Sub-tasks of ${p.wo_number}`,
            rows: subs,
            columns: [
                ["SUB WO #", "wo_number"], ["TITLE", "title"],
                ["TYPE", "type"], ["PRIORITY", "priority"], ["STATUS", (s) => status(s.status)],
                ["START", (s) => s.start_date || ""], ["FINISH", (s) => s.end_date || ""],
                ["PARTS COUNT", (s) => (s.parts_issued || []).length], ["COST (INR)", (s) => s.total_cost || 0],
            ],
        });
        // Row-wise work descriptions: parent + each sub, one row per entry.
        // Keeps Excel usable (no newlines crammed into one cell) and lets
        // readers sort/filter by date.
        const parentDescs = descList(p.descriptions).map((d) => ({
            source: `MAIN · ${p.wo_number}`,
            date: d.date || "",
            text: d.text || "",
        }));
        const subDescs = subs.flatMap((s) => descList(s.descriptions).map((d) => ({
            source: `SUB · ${s.wo_number}`,
            date: d.date || "",
            text: d.text || "",
        })));
        const allDescs = [...parentDescs, ...subDescs];
        if (allDescs.length) {
            sections.push({
                title: `  └ Work descriptions of ${p.wo_number} (dated)`,
                rows: allDescs,
                columns: [
                    ["WORK ORDER", "source"],
                    ["DATE", "date"],
                    ["DESCRIPTION", "text"],
                ],
            });
        }
    });
    const grand = parents.reduce((a, p) => a + grandTotal(p.sub_tasks || []), 0);
    sections.push({ title: "Grand total", rows: [{ t: grand }], columns: [["GRAND TOTAL (INR)", "t"]] });
    downloadMultiSectionCsv(filename, sections);
}
